/**
 * « Options & services » (super admin) — LOGIQUE PURE de l'activation d'une option
 * pour un client avec une FORMULE de facturation. Aucune I/O (cf. serviceActivationService.ts).
 *
 * Formules :
 *   - 'free'  : option offerte (gratuite, sans fin)
 *   - 'trial' : période de gratuité jusqu'à une date (calendrier), puis payant
 *   - 'paid'  : payant tout de suite
 *
 * Les options vendables = les modules du catalogue freemium (plans.ts) : fidélité,
 * avis Google, campagnes, YumLink Contrôle. L'activation pose un entitlement
 * (`tenant_features`) via la RPC `admin_set_tenant_option`.
 */
import { MODULE_OPTIONS, type ModuleKey } from './plans';

export type BillingMode = 'free' | 'trial' | 'paid';

export interface FormuleDef {
  key: BillingMode;
  label: string;
  /** Aide affichée sous le sélecteur. */
  hint: string;
  /** La formule exige-t-elle une date de fin (calendrier) ? */
  needsDate: boolean;
}

export const FORMULES: FormuleDef[] = [
  { key: 'free', label: 'Gratuit', hint: 'Option offerte, sans limite de durée.', needsDate: false },
  { key: 'trial', label: 'Essai gratuit (période)', hint: 'Gratuit jusqu’à la date choisie, puis facturé.', needsDate: true },
  { key: 'paid', label: 'Payant', hint: 'Facturé dès maintenant selon le tarif de l’option.', needsDate: false },
];

export interface ServiceDef {
  key: ModuleKey;
  emoji: string;
  title: string;
  /** Tarif à la carte (rappel). */
  priceLabel: string;
}

/** Catalogue des services activables (dérivé du catalogue freemium). */
export const SERVICES: ServiceDef[] = MODULE_OPTIONS.map((m) => ({
  key: m.key,
  emoji: m.emoji,
  title: m.title,
  priceLabel: m.soloPriceLabel,
}));

export function serviceLabel(key: ModuleKey): string {
  const s = SERVICES.find((x) => x.key === key);
  return s ? `${s.emoji} ${s.title}` : key;
}

export function formuleLabel(mode: BillingMode): string {
  return FORMULES.find((f) => f.key === mode)?.label ?? mode;
}

export interface ActivationInput {
  /** Activer (true) ou désactiver (false) l'option. */
  enabled: boolean;
  mode: BillingMode;
  /** Date de fin d'essai au format 'YYYY-MM-DD' (requis si mode='trial' et enabled). */
  trialDate?: string | null;
  /** Référence « aujourd'hui » (injectable pour les tests). */
  now?: Date;
}

export interface ActivationPayload {
  enabled: boolean;
  billingMode: BillingMode | null;
  /** ISO 8601 (fin de journée locale) ou null. */
  trialEndsAt: string | null;
}

export interface ActivationResult {
  ok: boolean;
  error?: string;
  payload?: ActivationPayload;
}

/**
 * Valide et construit le payload d'activation à envoyer à la RPC.
 * Règles : si on désactive, on ignore la formule. Si formule = essai, la date est
 * obligatoire et doit être dans le futur. La date de fin est fixée à 23:59:59 locale.
 */
export function buildActivation(input: ActivationInput): ActivationResult {
  if (!input.enabled) {
    return { ok: true, payload: { enabled: false, billingMode: null, trialEndsAt: null } };
  }

  if (input.mode === 'trial') {
    const raw = (input.trialDate ?? '').trim();
    if (!raw) return { ok: false, error: 'Choisis une date de fin d’essai.' };
    const end = endOfDay(raw);
    if (!end) return { ok: false, error: 'Date d’essai invalide.' };
    const now = input.now ?? new Date();
    if (end.getTime() <= now.getTime()) {
      return { ok: false, error: 'La date de fin d’essai doit être dans le futur.' };
    }
    return { ok: true, payload: { enabled: true, billingMode: 'trial', trialEndsAt: end.toISOString() } };
  }

  // free | paid : pas de date.
  return { ok: true, payload: { enabled: true, billingMode: input.mode, trialEndsAt: null } };
}

/** Convertit 'YYYY-MM-DD' en Date à 23:59:59.999 locale (fin de journée). null si invalide. */
export function endOfDay(yyyyMmDd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d), 23, 59, 59, 999);
  if (Number.isNaN(date.getTime())) return null;
  // Vérifie que la date est réelle (ex. 2026-02-31 → rejeté).
  if (date.getMonth() !== Number(mo) - 1) return null;
  return date;
}

/** Un essai est-il encore actif à la date `now` ? */
export function isTrialActive(trialEndsAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!trialEndsAt) return false;
  const end = new Date(trialEndsAt);
  if (Number.isNaN(end.getTime())) return false;
  return end.getTime() > now.getTime();
}

/** Libellé lisible de l'état de facturation d'une option active. */
export function billingStateLabel(
  mode: BillingMode | null | undefined,
  trialEndsAt: string | null | undefined,
  now: Date = new Date(),
): string {
  if (mode === 'free') return 'Gratuit';
  if (mode === 'paid') return 'Payant';
  if (mode === 'trial') {
    const end = trialEndsAt ? new Date(trialEndsAt) : null;
    if (!end || Number.isNaN(end.getTime())) return 'Essai';
    const d = end.toLocaleDateString('fr-FR');
    return isTrialActive(trialEndsAt, now) ? `Essai gratuit jusqu’au ${d}` : `Essai terminé le ${d}`;
  }
  return '—';
}
